import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import SettingProperties from "./setting-properties-text.mdx";
import NamingTip from "./naming-tip.mdx";
import Intro from "./intro.mdx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Intro mdxType="Intro" />
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`posthog.capture('user_signed_up')
`}</code></pre>
    <NamingTip mdxType="NamingTip" />
    <SettingProperties mdxType="SettingProperties" />
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`posthog.capture('user_signed_up', {
    login_type: "email",
    is_free_trial: true
})
`}</code></pre>
    <h3 {...{
      "id": "capturing-screen-views"
    }}>{`Capturing screen views`}</h3>
    <h4 {...{
      "id": "with-react-navigationnative-and-autocapture"
    }}>{`With `}<inlineCode parentName="h4">{`@react-navigation/native`}</inlineCode>{` and autocapture:`}</h4>
    <p>{`When using `}<a parentName="p" {...{
        "href": "https://reactnavigation.org/docs/6.x/getting-started"
      }}>{`@react-navigation/native`}</a>{` v6 or lower, screen tracking is automatically captured if the `}<a parentName="p" {...{
        "href": "/docs/libraries/react-native#autocapture"
      }}><inlineCode parentName="a">{`autocapture`}</inlineCode></a>{` property is used in the `}<inlineCode parentName="p">{`PostHogProvider`}</inlineCode>{`:`}</p>
    <p>{`It is important that the `}<inlineCode parentName="p">{`PostHogProvider`}</inlineCode>{` is configured as a child of the `}<inlineCode parentName="p">{`NavigationContainer`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`// App.(js|ts)

import { PostHogProvider } from 'posthog-react-native'
import { NavigationContainer } from '@react-navigation/native'

export function App() {
    return (
        <NavigationContainer>
            <PostHogProvider apiKey="<ph_project_api_key>" autocapture>
                {/* Rest of app */}
            </PostHogProvider>
        </NavigationContainer>
    )
}
`}</code></pre>
    <p>{`When using `}<a parentName="p" {...{
        "href": "https://reactnavigation.org/docs/7.x/getting-started"
      }}>{`@react-navigation/native`}</a>{` v7 or higher, screen tracking has to be manually captured:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`// App.(js|ts)

import { PostHogProvider } from 'posthog-react-native'
import { NavigationContainer } from '@react-navigation/native'

// Using \`PostHogProvider\` is optional, but needed if you want to capture touch events automatically with the \`captureTouches\` option.
export function App() {
    return (
        <NavigationContainer>
            <PostHogProvider apiKey="<ph_project_api_key>" autocapture={{
              captureScreens: false, // Screen events are handled differently for v7 and higher
              captureTouches: true,
            }}>
                {/* Rest of app */}
            </PostHogProvider>
        </NavigationContainer>
    )
}
`}</code></pre>
    <p>{`Check out and set it up the official way for `}<a parentName="p" {...{
        "href": "https://reactnavigation.org/docs/screen-tracking/"
      }}>{`Screen tracking for analytics`}</a>{`.`}</p>
    <p>{`Then call the `}<inlineCode parentName="p">{`screen`}</inlineCode>{` method within the `}<inlineCode parentName="p">{`trackScreenView`}</inlineCode>{` method.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`const posthog = usePostHog() // use the usePostHog hook if using the PostHogProvider or your own custom posthog instance
// you can read the params from \`getCurrentRoute()\`
posthog.screen(currentRouteName, params)
`}</code></pre>
    <h4 {...{
      "id": "with-react-native-navigation-and-autocapture"
    }}>{`With `}<inlineCode parentName="h4">{`react-native-navigation`}</inlineCode>{` and autocapture:`}</h4>
    <p>{`First, simplify the wrapping of your screens with a shared PostHogProvider:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`import PostHog, { PostHogProvider } from 'posthog-react-native'
import { Navigation } from 'react-native-navigation';

export const posthog = new PostHog('<ph_project_api_key>');

export const SharedPostHogProvider = (props: any) => {
  return (
    <PostHogProvider client={posthog} autocapture={{
      captureScreens: false, // Screen events are handled differently for react-native-navigation
      captureTouches: true,
    }}>
      {props.children}
    </PostHogProvider>
  );
};
`}</code></pre>
    <p>{`Then, every screen needs to be wrapped with this provider if you want to capture touches or use the `}<inlineCode parentName="p">{`usePostHog()`}</inlineCode>{` hook `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`export const MyScreen = () => {
  return (
    <SharedPostHogProvider>
      <View>
        ...
      </View>
    </SharedPostHogProvider>
  );
};

Navigation.registerComponent('Screen', () => MyScreen);

Navigation.events().registerAppLaunchedListener(async () => {
  posthog.initReactNativeNavigation({
    navigation: {
      // (Optional) Set the name based on the route. Defaults to the route name.
      routeToName: (name, properties) => name,
      // (Optional) Tracks all passProps as properties. Defaults to undefined
      routeToProperties: (name, properties) => properties,
    },
    captureScreens: true,
  });
});
`}</code></pre>
    <h4 {...{
      "id": "with-expo-router"
    }}>{`With `}<inlineCode parentName="h4">{`expo-router`}</inlineCode>{`:`}</h4>
    <p>{`Check out and set it up the official way for `}<a parentName="p" {...{
        "href": "https://docs.expo.dev/router/reference/screen-tracking/"
      }}>{`Screen tracking for analytics`}</a>{`.`}</p>
    <p>{`Then call the `}<inlineCode parentName="p">{`screen`}</inlineCode>{` method within the `}<inlineCode parentName="p">{`useEffect`}</inlineCode>{` callback.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`const posthog = usePostHog() // use the usePostHog hook if using the PostHogProvider or your own custom posthog instance
posthog.screen(pathname, params)
`}</code></pre>
    <h4 {...{
      "id": "manually-capturing-screen-capture-events"
    }}>{`Manually capturing screen capture events`}</h4>
    <p>{`If you prefer not to use autocapture, you can manually capture screen views by calling `}<inlineCode parentName="p">{`posthog.screen()`}</inlineCode>{`. This function requires a `}<inlineCode parentName="p">{`name`}</inlineCode>{`. You may also pass in an optional `}<inlineCode parentName="p">{`properties`}</inlineCode>{` object.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`posthog.screen('dashboard', {
    background: 'blue',
    hero: 'superhog',
})
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      